import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PrintIcon from '@mui/icons-material/Print';
import { previewCatalogTemplateHtml } from '../../../app/api';

const readIframeScroll = (iframe) => {
  try {
    const doc = iframe?.contentDocument;
    if (!doc) return { x: 0, y: 0 };
    return {
      x: doc.documentElement.scrollLeft || doc.body?.scrollLeft || 0,
      y: doc.documentElement.scrollTop || doc.body?.scrollTop || 0,
    };
  } catch {
    return { x: 0, y: 0 };
  }
};

const writeIframeScroll = (iframe, { x, y }) => {
  try {
    const doc = iframe?.contentDocument;
    if (!doc) return;
    doc.documentElement.scrollLeft = x;
    doc.documentElement.scrollTop = y;
    if (doc.body) {
      doc.body.scrollLeft = x;
      doc.body.scrollTop = y;
    }
  } catch {
    /* ignore */
  }
};

const CatalogPreviewIframe = ({
  catalogId,
  templateJson,
  instanceJson,
  previewMode,
  refreshKey,
  allowSynthetic = false,
}) => {
  const [html, setHtml] = useState('');
  const iframeRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const savedScrollRef = useRef({ outerX: 0, outerY: 0, innerX: 0, innerY: 0 });

  const captureScroll = useCallback(() => {
    const outer = scrollContainerRef.current;
    const inner = readIframeScroll(iframeRef.current);
    savedScrollRef.current = {
      outerX: outer?.scrollLeft ?? 0,
      outerY: outer?.scrollTop ?? 0,
      innerX: inner.x,
      innerY: inner.y,
    };
  }, []);

  const restoreScroll = useCallback(() => {
    const { outerX, outerY, innerX, innerY } = savedScrollRef.current;
    const outer = scrollContainerRef.current;
    if (outer) {
      outer.scrollLeft = outerX;
      outer.scrollTop = outerY;
    }
    writeIframeScroll(iframeRef.current, { x: innerX, y: innerY });
  }, []);

  const loadPreview = useCallback(async () => {
    if (!catalogId) return;
    captureScroll();
    try {
      const result = await previewCatalogTemplateHtml({
        catalog_id: catalogId,
        template_json: templateJson,
        instance_json: instanceJson,
        preview_mode: previewMode,
        allow_synthetic: allowSynthetic,
      });
      setHtml(result || '');
    } catch {
      setHtml('<p style="padding:24px;font-family:sans-serif">Preview unavailable</p>');
    }
  }, [catalogId, templateJson, instanceJson, previewMode, allowSynthetic, captureScroll]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void loadPreview();
    }, 300);
    return () => clearTimeout(timer);
  }, [loadPreview, refreshKey]);

  useEffect(() => {
    if (!html) return undefined;
    const iframe = iframeRef.current;
    if (!iframe) return undefined;

    const applyScroll = () => {
      requestAnimationFrame(() => {
        restoreScroll();
        requestAnimationFrame(restoreScroll);
      });
    };

    iframe.addEventListener('load', applyScroll);
    if (iframe.contentDocument?.readyState === 'complete') {
      applyScroll();
    }
    const delayedRestore = window.setTimeout(restoreScroll, 200);
    return () => {
      iframe.removeEventListener('load', applyScroll);
      window.clearTimeout(delayedRestore);
    };
  }, [html, restoreScroll]);

  const iframeWidth = useMemo(() => {
    if (previewMode === 'mobile') return 390;
    if (previewMode === 'print') return 794;
    return '100%';
  }, [previewMode]);

  return (
    <Box
      ref={scrollContainerRef}
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        height: '100%',
        bgcolor: 'grey.100',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'stretch',
        overflow: 'auto',
      }}
    >
      <Box
        component="iframe"
        ref={iframeRef}
        title="catalog-preview"
        srcDoc={html}
        sx={{
          width: iframeWidth,
          maxWidth: '100%',
          height: '100%',
          minHeight: 480,
          border: 0,
          bgcolor: '#fff',
          boxShadow: previewMode === 'mobile' ? 3 : 0,
        }}
      />
    </Box>
  );
};

export const PreviewModeToolbar = ({ mode, onChange }) => (
  <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, value) => value && onChange(value)}>
    <ToggleButton value="desktop" aria-label="desktop">
      <DesktopWindowsIcon fontSize="small" />
    </ToggleButton>
    <ToggleButton value="mobile" aria-label="mobile">
      <PhoneIphoneIcon fontSize="small" />
    </ToggleButton>
    <ToggleButton value="print" aria-label="print">
      <PrintIcon fontSize="small" />
    </ToggleButton>
  </ToggleButtonGroup>
);

export default CatalogPreviewIframe;
